import { cn } from '@/lib/utils';
import logoSrc from '/brand/sreerasa-logo.png';

interface LogoProps {
  className?: string;
  /** Renders the wordmark beside the mark. Off for tight spaces. */
  showWordmark?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizes = {
  sm: { mark: 'h-8', text: 'text-base' },
  md: { mark: 'h-11', text: 'text-xl' },
  lg: { mark: 'h-16', text: 'text-3xl' },
};

/**
 * The SreeRasa mark.
 *
 * The logo already contains the wordmark, so `showWordmark` is off by default
 * — setting it renders a text version *instead*, for places where the full
 * artwork is too detailed to read (favicons, dense nav, tiny footers).
 */
export function Logo({ className, showWordmark = false, size = 'md' }: LogoProps) {
  const scale = sizes[size];

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <img
        src={logoSrc}
        alt="SreeRasa"
        className={cn(scale.mark, 'w-auto object-contain select-none')}
        draggable={false}
        // The mark is above the fold in the header on every page — let the
        // browser prioritise it.
        fetchPriority="high"
      />
      {showWordmark && (
        <span className={cn('font-display font-black tracking-tight', scale.text)}>
          <span className="text-farm-600">SREE</span>
          <span className="text-harvest-500">RASA</span>
        </span>
      )}
    </span>
  );
}

/** Text-only lockup, for dark backgrounds where the artwork's yellow fights. */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn('font-display font-black tracking-tight', className)}>
      <span className="text-farm-500">SREE</span>
      <span className="text-harvest-400">RASA</span>
    </span>
  );
}
